import { useEffect, useState } from "react";
import { Button, Popconfirm, Spin, Table, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { getClientCountryFees, deleteClientCountryFee } from "../../services/ApiService";
import { hasPermission } from "../GlobalFunction";

// Per-country override of a client's fee fields -- confirmed with the
// user, 2026-10-16: real contract data shows fees genuinely differ by
// country of employment (e.g. Acumen's Setup Fee is 240 EUR in India but
// 500 USD in Nepal), which the main Client form's single set of fee
// fields can't represent. Those main fields stay the default/fallback;
// a row added here overrides them for one specific country.
//
// Purely a read + remove display -- adding/editing a country's fees
// happens via the "Country-wise Fees: Country" field in the Client_Onboard
// section above (reusing that section's own fee inputs), confirmed with
// the user, 2026-10-16: not a separate button/modal on this table.
// See UpdateClientForm.js's handleUpdate for the add/update logic.
const ClientCountryFees = ({ clientId }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const canEdit = hasPermission("client_list", "update");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await getClientCountryFees(clientId);
      if (res?.responseCode === 200) {
        setRows(res.country_fees || []);
      } else {
        message.error(res?.responseMessage || "Could not load country fees.");
      }
    } catch (error) {
      console.error("Error fetching country fees:", error);
      message.error("Could not load country fees.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clientId) return;
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleDelete = async (row) => {
    try {
      const res = await deleteClientCountryFee(clientId, row.id);
      if (res?.responseCode === 200) {
        message.success("Country fee removed");
        fetchRows();
      } else {
        message.error(res?.responseMessage || "Failed to remove country fee.");
      }
    } catch (error) {
      console.error("Error removing country fee:", error);
      message.error("Something went wrong. Please try again.");
    }
  };

  const columns = [
    { title: "Country", dataIndex: "country_name", key: "country_name" },
    { title: "Setup Fees", dataIndex: "client_setup_fees", key: "client_setup_fees", render: (v) => v || "—" },
    { title: "Deposit", dataIndex: "client_deposit", key: "client_deposit", render: (v) => v || "—" },
    {
      title: "Monthly Min Fees (Locals)",
      dataIndex: "client_monthly_minimum_fees_locals",
      key: "client_monthly_minimum_fees_locals",
      render: (v) => v || "—",
    },
  ];

  if (canEdit) {
    columns.push({
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Popconfirm
          title="Remove this country's fee entry?"
          onConfirm={() => handleDelete(row)}
          okText="Remove"
          cancelText="Cancel"
        >
          <Button type="link" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    });
  }

  return (
    <div className="Reviewsection">
      <h3 className="section-title">Country-wise Fees</h3>
      <p style={{ color: "#888", fontSize: "0.85rem", marginTop: 0 }}>
        Overrides this client's fee fields above for a specific country of employment. Add or edit one using
        the "Country-wise Fees: Country" field in the Client Onboard section above.
      </p>

      <Spin spinning={loading}>
        <Table
          rowKey="id"
          dataSource={rows}
          columns={columns}
          pagination={false}
          locale={{ emptyText: "No country-specific fees added yet." }}
          size="small"
        />
      </Spin>
    </div>
  );
};

export default ClientCountryFees;
